// Admin — Blog editor. 4 fixed categories. Markdown body.
import { useEffect, useRef, useState } from "react";
import { Plus, Trash2, Save, Loader2, Eye, EyeOff, FileText, ArrowLeft, Search, ImageIcon, Upload, BookOpen, ExternalLink, Sparkles, Languages, Map as MapIcon, CheckSquare, Square } from "lucide-react";
import { adminApi } from "@/lib/adminApi";

const CATEGORIES = [
  { slug: "wine_insights", label: "Wine insights" },
  { slug: "guides", label: "Guides" },
  { slug: "get_started", label: "Get started" },
  { slug: "for_professionals", label: "For professionals" },
  { slug: "ebook", label: "Ebook" },
  { slug: "cepage", label: "Cépages" },
];

const SUPPORTED_LANGS = ["fr", "en", "es", "pt"];

const formatBytes = (n) => {
  if (!n || n <= 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
};

const AdminBlogPage = () => {
  const [articles, setArticles] = useState([]);
  const [groupLangs, setGroupLangs] = useState({});  // slug → [langs]
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("");
  const [editing, setEditing] = useState(null);  // null | "new" | slug
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(null);  // null | "delete" | "cover-map" | "cover-nano" | "translate"
  const [bulkProgress, setBulkProgress] = useState(null);  // {done, total}
  const [bulkResult, setBulkResult] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const [d, g] = await Promise.all([
        adminApi.blogList(),
        adminApi.blogTranslationGroups().catch(() => ({ groups: {} })),
      ]);
      setArticles(d?.items || []);
      setGroupLangs(g?.groups || {});
      setSelected(new Set());
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  if (editing) {
    return (
      <ArticleEditor
        slug={editing === "new" ? null : editing}
        onClose={async (changed) => {
          setEditing(null);
          if (changed) await reload();
        }}
      />
    );
  }

  const filtered = articles.filter(
    (a) => !filter || a.title?.toLowerCase().includes(filter.toLowerCase()) || a.slug?.includes(filter),
  );

  const toggle = (slug) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(slug)) n.delete(slug); else n.add(slug);
      return n;
    });
  };

  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map((a) => a.slug)));
  };

  const runBulk = async (kind) => {
    const slugs = Array.from(selected);
    if (slugs.length === 0) return;
    setError(null); setBulkResult(null);
    setBulkBusy(kind);

    try {
      if (kind === "delete") {
        if (!window.confirm(`Supprimer ${slugs.length} article${slugs.length > 1 ? "s" : ""} ? Cette action est irréversible.`)) {
          setBulkBusy(null); return;
        }
        const r = await adminApi.blogBulkDelete(slugs);
        setBulkResult({ kind, message: `${r.deleted} article(s) supprimé(s).` });
      } else if (kind === "cover-map" || kind === "cover-nano") {
        const results = [];
        for (let i = 0; i < slugs.length; i++) {
          setBulkProgress({ done: i, total: slugs.length, slug: slugs[i] });
          try {
            if (kind === "cover-map") await adminApi.adminGenerateThumbnail(slugs[i]);
            else await adminApi.blogGenerateCoverNanoBanana(slugs[i]);
            results.push({ slug: slugs[i], ok: true });
          } catch (e) {
            results.push({ slug: slugs[i], ok: false, error: e.message });
          }
        }
        setBulkProgress(null);
        const ok = results.filter((r) => r.ok).length;
        setBulkResult({ kind, message: `${ok}/${slugs.length} couverture(s) régénérée(s).`, results });
      } else if (kind === "translate") {
        const results = [];
        for (let i = 0; i < slugs.length; i++) {
          setBulkProgress({ done: i, total: slugs.length, slug: slugs[i] });
          try {
            const r = await adminApi.blogTranslate(slugs[i], null);  // auto-detect missing
            results.push({ slug: slugs[i], ok: true, created: r.created || [], skipped: r.skipped || [] });
          } catch (e) {
            results.push({ slug: slugs[i], ok: false, error: e.message });
          }
        }
        setBulkProgress(null);
        const totalCreated = results.reduce((s, r) => s + (r.created?.length || 0), 0);
        setBulkResult({ kind, message: `${totalCreated} traduction(s) créée(s).`, results });
      }
      await reload();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBulkBusy(null);
      setBulkProgress(null);
    }
  };

  if (loading) return <div className="min-h-[40vh] flex items-center justify-center"><Loader2 className="animate-spin text-[color:var(--gv-pink)]" /></div>;

  const allSelected = filtered.length > 0 && selected.size === filtered.length;

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-5 pb-32" data-testid="admin-blog">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-1">Admin · Contenu</div>
          <h1 className="font-display text-3xl text-[color:var(--gv-ink)]">Blog</h1>
          <p className="text-[13px] text-[color:var(--gv-ink)]/65 mt-1">{articles.length} article{articles.length > 1 ? "s" : ""}</p>
        </div>
        <button onClick={() => setEditing("new")} className="btn-primary" data-testid="admin-blog-new">
          <Plus size={14} /> Nouvel article
        </button>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-[5px] text-[13px]">{error}</div>}
      {bulkResult && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 p-3 rounded-[5px] text-[13px]" data-testid="admin-blog-bulk-result">
          {bulkResult.message}
          {bulkResult.results?.some((r) => !r.ok) && (
            <ul className="mt-1 ml-4 list-disc text-[12px] text-amber-700">
              {bulkResult.results.filter((r) => !r.ok).map((r) => (
                <li key={r.slug}><span className="font-mono">{r.slug}</span> — {r.error}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Filter + Select all */}
      <div className="flex items-center gap-3">
        <button
          onClick={toggleAll}
          className="inline-flex items-center gap-1.5 px-3 h-10 border border-[color:var(--gv-line)] rounded-[5px] bg-white hover:border-[color:var(--gv-pink)] text-[13px]"
          data-testid="admin-blog-select-all"
          title={allSelected ? "Tout désélectionner" : "Tout sélectionner (filtre courant)"}
        >
          {allSelected ? <CheckSquare size={14} /> : <Square size={14} />}
          {allSelected ? "Tout désélectionner" : "Tout sélectionner"}
        </button>
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--gv-ink)]/40" />
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Rechercher dans les articles…"
            className="w-full h-10 pl-9 pr-3 rounded-[5px] border border-[color:var(--gv-line)] text-[13.5px]"
            data-testid="admin-blog-filter"
          />
        </div>
      </div>

      {/* List */}
      <div className="space-y-2">
        {filtered.map((a) => {
          const cat = CATEGORIES.find((c) => c.slug === a.category);
          const langs = groupLangs[a.slug] || [a.language].filter(Boolean);
          const checked = selected.has(a.slug);
          return (
            <div
              key={a.slug}
              className={`w-full text-left bg-white border rounded-[5px] p-4 flex items-center gap-3 transition-colors ${checked ? "border-[color:var(--gv-pink)] bg-[color:var(--gv-pink)]/5" : "border-[color:var(--gv-line)] hover:border-[color:var(--gv-pink)]"}`}
              data-testid={`admin-blog-row-${a.slug}`}
            >
              <button
                onClick={() => toggle(a.slug)}
                className="shrink-0 w-5 h-5 flex items-center justify-center rounded border border-[color:var(--gv-line)] bg-white hover:border-[color:var(--gv-pink)]"
                data-testid={`admin-blog-check-${a.slug}`}
                aria-label={checked ? "Désélectionner" : "Sélectionner"}
              >
                {checked ? <CheckSquare size={14} className="text-[color:var(--gv-pink)]" /> : null}
              </button>
              <div className="cursor-pointer flex items-center gap-3 flex-1 min-w-0" onClick={() => setEditing(a.slug)}>
                {a.cover_image_url ? (
                  <img src={a.cover_image_url} alt="" className="w-16 h-12 object-cover rounded-[5px]" />
                ) : (
                  <div className="w-16 h-12 bg-[color:var(--gv-mist)] rounded-[5px] flex items-center justify-center"><FileText size={16} className="text-[color:var(--gv-ink)]/30" /></div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-display text-[15px] text-[color:var(--gv-ink)] truncate">{a.title}</div>
                    {a.published ? (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-emerald-600"><Eye size={10} /> Publié</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-amber-600"><EyeOff size={10} /> Draft</span>
                    )}
                    <div className="flex items-center gap-1">
                      {SUPPORTED_LANGS.map((l) => (
                        <span
                          key={l}
                          className={`text-[9.5px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${langs.includes(l) ? "border-[color:var(--gv-pink)] text-[color:var(--gv-pink)] bg-[color:var(--gv-pink)]/5" : "border-[color:var(--gv-line)] text-[color:var(--gv-ink)]/30"}`}
                          title={langs.includes(l) ? `Disponible en ${l.toUpperCase()}` : `Manquant en ${l.toUpperCase()}`}
                        >
                          {l}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="text-[11.5px] text-[color:var(--gv-ink)]/55 mt-0.5 flex items-center gap-2">
                    <span>{cat?.label || a.category}</span>
                    <span>·</span>
                    <span className="font-mono">{a.slug}</span>
                    {a.read_minutes && (<><span>·</span><span>{a.read_minutes} min de lecture</span></>)}
                  </div>
                </div>
              </div>
              {a.slug && a.published && (
                <a
                  href={`/journal/${a.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="p-1.5 rounded hover:bg-[color:var(--gv-mist)] text-[color:var(--gv-ink)]/50 hover:text-[color:var(--gv-pink)] shrink-0"
                  title="Voir sur geovinum.io"
                  data-testid={`admin-blog-view-${a.slug}`}
                >
                  <ExternalLink size={14} />
                </a>
              )}
            </div>
          );
        })}
        {filtered.length === 0 && <div className="text-center py-12 text-[color:var(--gv-ink)]/55">Aucun article. Créez le premier.</div>}
      </div>

      {/* Bulk action bar — sticky bottom */}
      {selected.size > 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 bg-white border border-[color:var(--gv-pink)] shadow-2xl rounded-[8px] px-4 py-3 flex items-center gap-3 flex-wrap max-w-[95vw]" data-testid="admin-blog-bulk-bar">
          <span className="text-[13px] font-medium text-[color:var(--gv-ink)]">
            {selected.size} sélectionné{selected.size > 1 ? "s" : ""}
          </span>
          <div className="h-6 w-px bg-[color:var(--gv-line)]" />
          <button
            onClick={() => runBulk("cover-map")}
            disabled={!!bulkBusy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12.5px] border border-[color:var(--gv-line)] rounded-[5px] bg-white hover:border-[color:var(--gv-pink)] hover:text-[color:var(--gv-pink)] disabled:opacity-40 transition-colors"
            data-testid="admin-blog-bulk-cover-map"
            title="Régénérer les couvertures depuis la carte geoVINUM"
          >
            {bulkBusy === "cover-map" ? <Loader2 size={13} className="animate-spin" /> : <MapIcon size={13} />}
            Régén. visuel (carte)
          </button>
          <button
            onClick={() => runBulk("cover-nano")}
            disabled={!!bulkBusy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12.5px] border border-[color:var(--gv-line)] rounded-[5px] bg-white hover:border-[color:var(--gv-pink)] hover:text-[color:var(--gv-pink)] disabled:opacity-40 transition-colors"
            data-testid="admin-blog-bulk-cover-nano"
            title="Régénérer les couvertures via Nano Banana (Gemini)"
          >
            {bulkBusy === "cover-nano" ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
            Régén. visuel (Nano Banana)
          </button>
          <button
            onClick={() => runBulk("translate")}
            disabled={!!bulkBusy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12.5px] border border-[color:var(--gv-line)] rounded-[5px] bg-white hover:border-[color:var(--gv-pink)] hover:text-[color:var(--gv-pink)] disabled:opacity-40 transition-colors"
            data-testid="admin-blog-bulk-translate"
            title="Créer les traductions dans les langues manquantes (FR/EN/ES/PT)"
          >
            {bulkBusy === "translate" ? <Loader2 size={13} className="animate-spin" /> : <Languages size={13} />}
            Traduire langues manquantes
          </button>
          <div className="h-6 w-px bg-[color:var(--gv-line)]" />
          <button
            onClick={() => runBulk("delete")}
            disabled={!!bulkBusy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12.5px] border border-red-200 text-red-600 hover:bg-red-50 rounded-[5px] disabled:opacity-40 transition-colors"
            data-testid="admin-blog-bulk-delete"
          >
            {bulkBusy === "delete" ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
            Supprimer
          </button>
          <button
            onClick={() => setSelected(new Set())}
            disabled={!!bulkBusy}
            className="text-[12px] text-[color:var(--gv-ink)]/55 hover:text-[color:var(--gv-ink)] px-2"
            data-testid="admin-blog-bulk-clear"
          >
            Annuler sélection
          </button>
          {bulkProgress && (
            <div className="w-full mt-1 text-[11px] text-[color:var(--gv-ink)]/65">
              {bulkProgress.done + 1}/{bulkProgress.total} · <span className="font-mono">{bulkProgress.slug}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ArticleEditor = ({ slug, onClose }) => {
  const isNew = !slug;
  const [form, setForm] = useState({
    title: "", excerpt: "", body_markdown: "", cover_image_url: "", author: "",
    category: "wine_insights", tags: [], language: "fr", published: false,
    appellation: "", geovinum_enabled: false,
    ebook_pdf_filename: "", ebook_pdf_size_bytes: 0, ebook_pages: 0,
  });
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [actualSlug, setActualSlug] = useState(slug);

  useEffect(() => {
    if (isNew) return;
    let cancelled = false;
    (async () => {
      try {
        const all = await adminApi.blogList();
        const doc = (all?.items || []).find((a) => a.slug === slug);
        if (!cancelled && doc) {
          setForm({
            title: doc.title || "",
            excerpt: doc.excerpt || "",
            body_markdown: doc.body_markdown || "",
            cover_image_url: doc.cover_image_url || "",
            author: doc.author || "",
            category: doc.category || "wine_insights",
            tags: doc.tags || [],
            language: doc.language || "fr",
            published: !!doc.published,
            appellation: doc.appellation || "",
            geovinum_enabled: !!doc.geovinum_enabled,
            ebook_pdf_filename: doc.ebook_pdf_filename || "",
            ebook_pdf_size_bytes: doc.ebook_pdf_size_bytes || 0,
            ebook_pages: doc.ebook_pages || 0,
          });
        }
      } catch (e) { if (!cancelled) setError(e.message); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [slug, isNew]);

  const save = async () => {
    setSaving(true); setError(null);
    try {
      if (isNew) {
        const created = await adminApi.blogCreate(form);
        setActualSlug(created.slug);
        await onClose(true);
      } else {
        await adminApi.blogUpdate(actualSlug, form);
        await onClose(true);
      }
    } catch (e) { setError(e.message || String(e)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Supprimer "${form.title}" ? Cette action est irréversible.`)) return;
    try { await adminApi.blogDelete(actualSlug); await onClose(true); }
    catch (e) { setError(e.message); }
  };

  const [generatingThumb, setGeneratingThumb] = useState(false);
  const [generatingNano, setGeneratingNano] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [translateMsg, setTranslateMsg] = useState(null);
  const regenerateThumbnail = async () => {
    if (!form.geovinum_enabled || !form.appellation) {
      setError("Activez la carte geoVINUM et renseignez l'appellation avant de générer la miniature.");
      return;
    }
    setGeneratingThumb(true); setError(null);
    try {
      const res = await adminApi.adminGenerateThumbnail(actualSlug);
      if (res?.cover_image_url) {
        setForm((f) => ({ ...f, cover_image_url: res.cover_image_url }));
      }
    } catch (e) { setError(e.message || String(e)); }
    finally { setGeneratingThumb(false); }
  };

  const regenerateNanoBanana = async () => {
    if (!actualSlug) { setError("Enregistrez d'abord l'article."); return; }
    if (!form.title) { setError("Un titre est requis pour générer une couverture Nano Banana."); return; }
    setGeneratingNano(true); setError(null);
    try {
      const res = await adminApi.blogGenerateCoverNanoBanana(actualSlug);
      if (res?.cover_image_url) setForm((f) => ({ ...f, cover_image_url: res.cover_image_url }));
    } catch (e) { setError(e.message || String(e)); }
    finally { setGeneratingNano(false); }
  };

  const translateMissing = async () => {
    if (!actualSlug) { setError("Enregistrez d'abord l'article."); return; }
    setTranslating(true); setError(null); setTranslateMsg(null);
    try {
      const res = await adminApi.blogTranslate(actualSlug, null);
      const created = res?.created || [];
      if (created.length === 0) {
        setTranslateMsg("Aucune langue manquante à traduire.");
      } else {
        setTranslateMsg(`${created.length} traduction(s) créée(s) : ${created.map((c) => c.language.toUpperCase()).join(", ")}. Elles sont en brouillon dans la liste.`);
      }
    } catch (e) { setError(e.message || String(e)); }
    finally { setTranslating(false); }
  };

  // Ebook upload — accepts PDF, auto-renders page 1 as cover thumbnail.
  const fileInputRef = useRef(null);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const onPdfPicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";  // allow re-picking same filename
    if (!file) return;
    if (!actualSlug) { setError("Enregistrez d'abord l'article (titre + Créer) avant d'attacher un PDF."); return; }
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      setError("Seuls les fichiers PDF sont acceptés."); return;
    }
    setUploadingPdf(true); setError(null);
    try {
      const res = await adminApi.adminEbookUpload(actualSlug, file);
      setForm((f) => ({
        ...f,
        category: "ebook",
        ebook_pdf_filename: res?.filename || file.name,
        ebook_pdf_size_bytes: res?.size_bytes || file.size,
        ebook_pages: res?.pages || 0,
        cover_image_url: res?.cover_image_url || f.cover_image_url,
      }));
    } catch (err) { setError(err.message || String(err)); }
    finally { setUploadingPdf(false); }
  };
  const deletePdf = async () => {
    if (!actualSlug || !form.ebook_pdf_filename) return;
    if (!window.confirm("Supprimer le PDF attaché ? Les liens de téléchargement déjà envoyés cesseront de fonctionner.")) return;
    try {
      await adminApi.adminEbookDelete(actualSlug);
      setForm((f) => ({ ...f, ebook_pdf_filename: "", ebook_pdf_size_bytes: 0, ebook_pages: 0 }));
    } catch (err) { setError(err.message || String(err)); }
  };

  if (loading) return <div className="min-h-[40vh] flex items-center justify-center"><Loader2 className="animate-spin text-[color:var(--gv-pink)]" /></div>;

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-5" data-testid="admin-blog-editor">
      <div className="flex items-center justify-between">
        <button onClick={() => onClose(false)} className="inline-flex items-center gap-2 text-[13px] text-[color:var(--gv-ink)]/65 hover:text-[color:var(--gv-ink)]" data-testid="admin-blog-back">
          <ArrowLeft size={14} /> Retour
        </button>
        <div className="flex items-center gap-2">
          {!isNew && (
            <button
              onClick={translateMissing}
              disabled={translating}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-[5px] border border-[color:var(--gv-line)] bg-white hover:border-[color:var(--gv-pink)] hover:text-[color:var(--gv-pink)] text-[13px] disabled:opacity-40"
              data-testid="admin-blog-translate"
              title="Créer des traductions dans les langues manquantes (FR/EN/ES/PT)"
            >
              {translating ? <Loader2 size={13} className="animate-spin" /> : <Languages size={13} />}
              Traduire manquantes
            </button>
          )}
          {!isNew && (
            <button onClick={remove} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-[5px] border border-red-200 text-red-600 hover:bg-red-50 text-[13px]" data-testid="admin-blog-delete">
              <Trash2 size={13} /> Supprimer
            </button>
          )}
          <button onClick={save} disabled={saving || !form.title} className="btn-primary" data-testid="admin-blog-save">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} {isNew ? "Créer" : "Enregistrer"}
          </button>
        </div>
      </div>

      <h1 className="font-display text-2xl text-[color:var(--gv-ink)]">{isNew ? "Nouvel article" : "Éditer l'article"}</h1>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-[5px] text-[13px]" data-testid="admin-blog-editor-error">{error}</div>}
      {translateMsg && <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 p-3 rounded-[5px] text-[13px]" data-testid="admin-blog-translate-msg">{translateMsg}</div>}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} placeholder="Titre de l'article" className="gv-input md:col-span-2" data-testid="admin-blog-title" />
        <select value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} className="gv-input" data-testid="admin-blog-category">
          {CATEGORIES.map((c) => <option key={c.slug} value={c.slug}>{c.label}</option>)}
        </select>
      </div>

      <textarea value={form.excerpt} onChange={(e) => setForm({...form, excerpt: e.target.value})} placeholder="Extrait (1-2 phrases pour la liste / SEO)" className="gv-input min-h-[60px]" data-testid="admin-blog-excerpt" />

      <div>
        <input value={form.cover_image_url} onChange={(e) => setForm({...form, cover_image_url: e.target.value})} placeholder="URL image de couverture" className="gv-input w-full" data-testid="admin-blog-cover" />
        {form.cover_image_url && (
          <div className="mt-2 flex items-center gap-3">
            <img src={form.cover_image_url} alt="Aperçu couverture" className="w-32 h-18 object-cover rounded border border-[color:var(--gv-line)]" />
            <span className="text-[11px] text-[color:var(--gv-ink)]/50 truncate">{form.cover_image_url}</span>
          </div>
        )}
        <button
          type="button"
          onClick={regenerateThumbnail}
          disabled={generatingThumb || !form.geovinum_enabled || !form.appellation || !actualSlug}
          className="mt-2 mr-2 inline-flex items-center gap-2 px-3 py-1.5 text-[12.5px] border border-[color:var(--gv-line)] rounded-[5px] bg-white hover:border-[color:var(--gv-pink)] hover:text-[color:var(--gv-pink)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          data-testid="admin-blog-generate-thumbnail"
          title={!form.geovinum_enabled || !form.appellation ? "Activez la carte + appellation, puis enregistrez avant de générer" : "Capturer la carte geoVINUM comme miniature"}
        >
          {generatingThumb ? <Loader2 size={13} className="animate-spin" /> : <ImageIcon size={13} />}
          {generatingThumb ? "Capture en cours (~10s)…" : "Carte geoVINUM"}
        </button>
        <button
          type="button"
          onClick={regenerateNanoBanana}
          disabled={generatingNano || !form.title || !actualSlug}
          className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 text-[12.5px] border border-[color:var(--gv-line)] rounded-[5px] bg-white hover:border-[color:var(--gv-pink)] hover:text-[color:var(--gv-pink)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          data-testid="admin-blog-generate-nanobanana"
          title="Générer la couverture via Nano Banana (Gemini)"
        >
          {generatingNano ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
          {generatingNano ? "Génération…" : "Nano Banana"}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <input value={form.author} onChange={(e) => setForm({...form, author: e.target.value})} placeholder="Auteur" className="gv-input" />
        <select value={form.language} onChange={(e) => setForm({...form, language: e.target.value})} className="gv-input">
          <option value="fr">FR</option><option value="en">EN</option><option value="es">ES</option><option value="pt">PT</option>
        </select>
        <input value={form.tags.join(", ")} onChange={(e) => setForm({...form, tags: e.target.value.split(",").map((t) => t.trim()).filter(Boolean)})} placeholder="Tags (séparés par virgule)" className="gv-input" />
      </div>

      <textarea
        value={form.body_markdown}
        onChange={(e) => setForm({...form, body_markdown: e.target.value})}
        placeholder="Corps de l'article en Markdown… (titres avec #, listes avec -, gras avec **texte**, liens avec [label](url))"
        className="gv-input font-mono text-[13px] min-h-[400px]"
        data-testid="admin-blog-body"
      />

      <label className="flex items-center gap-2 text-[13px]">
        <input type="checkbox" checked={form.published} onChange={(e) => setForm({...form, published: e.target.checked})} data-testid="admin-blog-published" />
        <span>Publié (visible côté public)</span>
      </label>

      {/* Ebook PDF — visible only when category = "ebook". The PDF gates a
          lead-capture form on the public article (email + RGPD + newsletter). */}
      {form.category === "ebook" && (
        <div className="bg-[color:var(--gv-mist)]/60 border border-[color:var(--gv-line)] rounded-[5px] p-4 space-y-3" data-testid="admin-blog-ebook-section">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-pink)] mb-1 inline-flex items-center gap-1.5"><BookOpen size={11} /> Fichier ebook (PDF)</div>
              <p className="text-[12px] text-[color:var(--gv-ink)]/65">Le PDF est servi derrière un token. La couverture est générée automatiquement depuis la page 1.</p>
            </div>
            <input ref={fileInputRef} type="file" accept="application/pdf,.pdf" onChange={onPdfPicked} className="hidden" data-testid="admin-blog-ebook-file" />
            {!form.ebook_pdf_filename ? (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingPdf || !actualSlug}
                className="inline-flex items-center gap-2 px-3 py-2 text-[12.5px] border border-[color:var(--gv-line)] rounded-[5px] bg-white hover:border-[color:var(--gv-pink)] hover:text-[color:var(--gv-pink)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors shrink-0"
                data-testid="admin-blog-ebook-upload"
                title={!actualSlug ? "Enregistrez l'article avant d'attacher un PDF" : "Choisir un fichier PDF"}
              >
                {uploadingPdf ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                {uploadingPdf ? "Téléversement…" : "Téléverser un PDF"}
              </button>
            ) : (
              <div className="flex items-center gap-2 shrink-0">
                <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploadingPdf} className="inline-flex items-center gap-1.5 px-3 py-2 text-[12.5px] border border-[color:var(--gv-line)] rounded-[5px] bg-white hover:border-[color:var(--gv-pink)] hover:text-[color:var(--gv-pink)] transition-colors" data-testid="admin-blog-ebook-replace">
                  {uploadingPdf ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Remplacer
                </button>
                <button type="button" onClick={deletePdf} className="inline-flex items-center gap-1.5 px-3 py-2 text-[12.5px] border border-red-200 text-red-600 hover:bg-red-50 rounded-[5px] transition-colors" data-testid="admin-blog-ebook-delete">
                  <Trash2 size={13} /> Retirer
                </button>
              </div>
            )}
          </div>
          {form.ebook_pdf_filename && (
            <div className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-3 flex items-center gap-3 text-[12.5px]" data-testid="admin-blog-ebook-info">
              <div className="w-10 h-12 bg-[color:var(--gv-pink)]/10 rounded flex items-center justify-center shrink-0">
                <BookOpen size={18} className="text-[color:var(--gv-pink)]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="truncate text-[color:var(--gv-ink)] font-medium">{form.ebook_pdf_filename}</div>
                <div className="text-[color:var(--gv-ink)]/55 mt-0.5">
                  {formatBytes(form.ebook_pdf_size_bytes)}
                  {form.ebook_pages > 0 && <> · {form.ebook_pages} page{form.ebook_pages > 1 ? "s" : ""}</>}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* geoVINUM appellation map — auto-injected in the article if enabled. */}
      <div className="bg-[color:var(--gv-mist)]/60 border border-[color:var(--gv-line)] rounded-[5px] p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-pink)] mb-1">Carte geoVINUM</div>
            <p className="text-[12px] text-[color:var(--gv-ink)]/65">Si activé, la carte de l&apos;appellation s&apos;affiche automatiquement à côté de l&apos;article.</p>
          </div>
          <label className="inline-flex items-center gap-2 text-[13px] shrink-0">
            <input type="checkbox" checked={form.geovinum_enabled} onChange={(e) => setForm({...form, geovinum_enabled: e.target.checked})} data-testid="admin-blog-geovinum-enabled" />
            <span>Afficher la carte</span>
          </label>
        </div>
        <input
          value={form.appellation}
          onChange={(e) => setForm({...form, appellation: e.target.value})}
          placeholder="Appellation (ex: Sancerre, Chablis, Côte-Rôtie)"
          className="gv-input w-full"
          data-testid="admin-blog-appellation"
        />
      </div>
    </div>
  );
};

export default AdminBlogPage;
